import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ } from "@/constants/testIds";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const faqs = [
  {
    question: "What is the Grassroots Psychological Protocol?",
    answer:
      "The Grassroots Protocol is our structured diagnostic mapping pathway comprising four phases: Clinical Intake & History Mapping, Psychometric Battery Administration, Multi-Informant Data Synthesis, and Biopsychosocial Formulation. This observer-led methodology ensures precision diagnostic architecture using gold-standard instrumentation.",
  },
  {
    question: "What does 'Observer-Led' clinical model mean?",
    answer:
      "Our Observer-Led model prioritizes clinician-administered observation in controlled environments over retrospective parent-report questionnaires. This approach minimizes recall bias, defensive reporting, and nervous system load confounds—yielding more accurate behavioral baselines for biopsychosocial formulation.",
  },
  {
    question: "What instruments are used for diagnostic mapping?",
    answer:
      "We utilize internationally validated gold-standard instruments including ADOS-2 (autism spectrum mapping), WISC-IV/V (cognitive architecture profiling), Conners 4 and BRIEF-2 (executive functioning indices), Vineland-3 (adaptive behavior), and others selected based on presenting concerns.",
  },
  {
    question: "How long does comprehensive diagnostic mapping take?",
    answer:
      "A full biopsychosocial assessment typically requires 1-3 psychometric sessions following the initial 60-minute clinical intake. Timeline depends on presentation complexity and battery requirements. Most assessments conclude with a formulation feedback session within 2-3 weeks.",
  },
  {
    question: "What is the difference between Assessments and Services?",
    answer:
      "Assessments provide pure psychometric evaluation and diagnostic mapping. Services provide ongoing therapeutic management including behavioral engineering, parenting architecture, and family system reconstruction based on your child's biopsychosocial profile.",
  },
  {
    question: "Are assessment fees and report writing included?",
    answer:
      "Clinical assessments and formal report writing are billed separately. Fees are customized based on the specific testing battery required. During your clinical intake, we will discuss the recommended assessment pathway and provide transparent fee information.",
  },
  {
    question: "Do you provide statutory disability certificates?",
    answer:
      "Our biopsychosocial formulation reports are designed for clinical case formulation, therapeutic planning, and educational support. They do not constitute statutory Medico-Legal Disability Certificates under the RPwD Act (2016) or RCI Act (1992). We can guide you on obtaining statutory certification if required.",
  },
  {
    question: "What is Behavioral Engineering?",
    answer:
      "Behavioral Engineering refers to our evidence-based intervention approach translating diagnostic mapping data into actionable therapeutic protocols. This includes ABA (Applied Behavior Analysis), PBS (Positive Behavior Support), sensory modulation, and other modalities tailored to the child's biopsychosocial profile.",
  },
];

export const FAQPage = () => {
  return (
    <div data-testid={FAQ.section}>
      <Helmet>
        <title>FAQs | Modern Minds - Child Psychology Questions Answered</title>
        <meta name="description" content="Frequently asked questions about child psychology assessments, the Grassroots Protocol, session duration, fees, and what to expect at Modern Minds, Lucknow." />
        <link rel="canonical" href="https://www.modernminds.co.in/faq" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <motion.div variants={fadeInUp} className="gradient-divider mx-auto mb-6" />
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            >
              Clinical Information
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-base sm:text-lg text-[#E2E8F0] max-w-2xl mx-auto"
            >
              Essential information regarding our diagnostic methodology and clinical architecture.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Accordion
            type="single"
            collapsible
            data-testid={FAQ.accordion}
            className="space-y-3"
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <AccordionItem
                  value={`item-${faq.question}`}
                  className="glass-card px-5 py-1 data-[state=open]:ring-1 data-[state=open]:ring-[#C9A962]/30"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${index}`}
                    className="font-heading text-base text-white hover:text-[#C9A962] py-4 text-left [&[data-state=open]>svg]:text-[#C9A962] hover:no-underline"
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-sm leading-relaxed text-[#E0E1DD] pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 text-center"
          >
            <div className="gradient-divider mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
              Additional Inquiries
            </h2>
            <p className="font-body text-sm text-[#E0E1DD] mb-6 max-w-xl mx-auto">
              For questions not addressed above or to discuss specific clinical presentations, schedule a consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center btn-outline rounded px-6 py-3 font-body font-medium text-sm"
              >
                Contact Us
              </a>
              <a
                href="https://consult.modernminds.co.in"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 btn-gold rounded px-6 py-3 font-body font-semibold text-sm"
              >
                Schedule Consultation
                <ArrowRight size={14} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
